import {Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {BaseChart, ChartAnnotation, ChartColor, ChartData} from '../../models/chart';
import {formatDate, formatNumber} from '@angular/common';
import {Chart} from '../../adapters/chart';

@Component({
  selector: 'app-base-chart',
  templateUrl: './base-chart.component.html',
  styleUrls: ['./base-chart.component.scss']
})
export class BaseChartComponent implements OnInit, OnDestroy, OnChanges {
  @Input() loading: boolean;
  @Input() error?: string;
  @Input() data: ChartData[];
  @Input() annotations: ChartAnnotation;
  @Input() color: ChartColor;

  @ViewChild('chart', { static: true }) chartElement: ElementRef;
  private _chartObj: BaseChart;

  ngOnInit(): void {
    this.renderChart();
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.data?.currentValue && changes?.data?.currentValue !== changes?.data?.previousValue) {
      this.renderChart();
    }
  }

  destroyChart() {
    this._chartObj?.destroy();
    this._chartObj = null;
  }

  renderChart() {
    if (this.loading || this.error || !this.data) {
      return;
    }

    if (this._chartObj) {
      this.updateChart();
    } else {
      this.createChart();
    }
  }

  updateChart() {
    if (!this._chartObj) {
      return;
    }

    const annotations = this.annotations ? {annotations: this.annotations}  : {};

    this._chartObj?.update({
      series: this.data,
      colors: this?.getColors(),
      ...annotations
    });
  }

  createChart() {
    this._chartObj?.destroy();

    if (!this.chartElement.nativeElement) {
      return;
    }

    this._chartObj = new Chart(
      this.chartElement.nativeElement,
      this.getChartOptions()
    );

    this._chartObj.render();
  }

  getTooltipTemplate = ({ seriesIndex, dataPointIndex, w }) => {
    const point = w.config.series[seriesIndex].data[dataPointIndex];
    const x = formatDate(point.x, 'MMM dd, YYYY', 'en');
    return `<div class="apexchart-tooltip">
    <div class="text-gray">${x}</div>
    <div class="y">${formatNumber(point.y, 'en', '0.2-6')}</div>
    <div>`;
  }

  getChart() {
    return {
      height: '300',
      width: '100%',
      zoom: {
        enabled: false,
      },
      toolbar: {
        show: false,
      }
    };
  }

  getGrid() {
    return {
      borderColor: '#343A40'
    };
  }

  getStroke() {
    return {
      curve: 'smooth',
      width: 2
    };
  }

  getColors(): any[] {
    return [this?.color];
  }

  getPlotOptions() {
    return {};
  }

  getDataLabels() {
    return {
      enabled: false,
    };
  }

  getYAxis(): any {
    return {
      labels: {
        formatter: (val) => {
          return `${formatNumber(val, 'en', '0.0')}`;
        },
        padding: 12,
        style: {
          colors: '#DDD'
        }
      },
    };
  }

  getXAxis() {
    return {
      type: 'datetime',
      tooltip: {
        enabled: false
      },
      axisBorder: {
        color: '#6C757D'
      },
      axisTicks: {
        show: false,
      },
      labels: {
        datetimeUTC: false,
        style: {
          colors: ['#DDD']
        }
      }
    };
  }

  getTooltip() {
    return {
      enabled: true,
      theme: 'cumulative-pnl-chart',
      intersect: false,
      custom: this.getTooltipTemplate
    };
  }

  getNoData() {
    return {
      text: this.loading ? 'Loading...' : 'No PnL data'
    };
  }

  getLegend() {
    return {
      show: false
    };
  }

  getChartOptions() {
    return {
      series: this?.data,
      annotations: this?.annotations,
      chart: this.getChart(),
      grid: this.getGrid(),
      stroke: this.getStroke(),
      colors: this.getColors(),
      plotOptions: this.getPlotOptions(),
      dataLabels: this.getDataLabels(),
      yaxis: this.getYAxis(),
      xaxis: this.getXAxis(),
      tooltip: this.getTooltip(),
      noData: this.getNoData(),
      legend: this.getLegend(),
    };
  }

}
